<template>
  <div class="s-footer-menu">
    <div class="s-footer-menu__accordion">
      <PAccordion
        collapse-icon="fas fa-chevron-up"
        expand-icon="fas fa-chevron-down"
        multiple
        transparent
      >
        <PAccordionPanel v-if="$slots.contact" value="contact">
          <PAccordionHeader class="s-footer-menu__title">
            {{ tr("main_site.layout.footer_contact_title") }}
          </PAccordionHeader>
          <PAccordionContent>
            <slot name="contact" />
          </PAccordionContent>
        </PAccordionPanel>

        <PAccordionPanel
          v-for="group in menuGroups"
          :key="group.value"
          :value="group.value"
        >
          <PAccordionHeader class="s-footer-menu__title">
            {{ group.title }}
          </PAccordionHeader>
          <PAccordionContent>
            <ul class="s-footer-menu__items">
              <li v-for="link in group.links" :key="link.href">
                <component
                  :is="linkTag"
                  class="s-footer-menu__items__link"
                  v-bind="linkProps(link.href, link.external)"
                >
                  {{ link.label }}
                </component>
              </li>
            </ul>
          </PAccordionContent>
        </PAccordionPanel>
      </PAccordion>
    </div>

    <div class="s-footer-menu__columns">
      <div
        v-if="$slots.contact"
        class="s-footer-menu__column s-footer-menu__column--contact"
      >
        <h3 class="s-footer-menu__title">
          {{ tr("main_site.layout.footer_contact_title") }}
        </h3>
        <slot name="contact" />
      </div>

      <div
        v-for="group in menuGroups"
        :key="group.value"
        class="s-footer-menu__column"
      >
        <h3 class="s-footer-menu__title">{{ group.title }}</h3>
        <ul class="s-footer-menu__items">
          <li v-for="link in group.links" :key="link.href">
            <component
              :is="linkTag"
              class="s-footer-menu__items__link"
              v-bind="linkProps(link.href, link.external)"
            >
              {{ link.label }}
            </component>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Component } from "vue";

import PAccordionContent from "primevue/accordioncontent";
import PAccordionHeader from "primevue/accordionheader";
import PAccordionPanel from "primevue/accordionpanel";
import { computed } from "vue";

import { ziggyUnsafe } from "@solvari/common-fe/integrations";
import { useI18nMainSiteLayout } from "@solvari/translations";
import { getEnv } from "@solvari/utils";

import PAccordion from "@/atoms/PAccordion.vue";

const props = defineProps<{ linkComponent?: Component }>();

const { tr } = useI18nMainSiteLayout();

const locale = getEnv().config.localeCode;

const linkTag = computed(() => props.linkComponent ?? "a");
function linkProps(url: string, external = false) {
  return props.linkComponent ? { to: url, external } : { href: url };
}

function menuLink(
  routeName: string,
  label: string,
  params?: Record<string, number | string | undefined>,
  searchParams?: Record<string, unknown>,
) {
  return {
    href: ziggyUnsafe(routeName, params, searchParams),
    label,
    external: !routeName.startsWith("mainSite."),
  };
}

const menuGroups = computed(() => [
  {
    value: "consumers",
    title: tr("main_site.layout.footer_menu_consumers.title"),
    links: [
      menuLink(
        "mainSite.productOverview",
        tr("main_site.layout.footer_menu_consumers.products"),
      ),
      ...(locale === "nl-NL"
        ? [
            menuLink(
              "client.slug",
              tr("main_site.layout.footer_menu_consumers.business_services"),
              {
                slug: tr(
                  "main_site.layout.footer_menu_consumers.business_services_slug",
                ),
              },
            ),
          ]
        : []),
      menuLink(
        "mainSite.companyWall",
        tr("main_site.layout.footer_menu_consumers.company_wall"),
        { page: undefined },
      ),
      menuLink(
        "auth.login",
        tr("main_site.layout.footer_menu_consumers.login"),
        undefined,
        { project: "mySolvari" },
      ),
    ],
  },
  {
    value: "companies",
    title: tr("main_site.layout.footer_menu_companies.title"),
    links: [
      menuLink(
        "client.pro.customer_platform",
        tr("main_site.layout.footer_menu_companies.pro_home"),
      ),
      menuLink(
        "client.pro.customer_registration",
        tr("main_site.layout.footer_menu_companies.register"),
      ),
      menuLink(
        "client.affiliate",
        tr("main_site.layout.footer_menu_companies.affiliates"),
      ),
      menuLink(
        "auth.login",
        tr("main_site.layout.footer_menu_companies.login"),
        undefined,
        { project: "customer" },
      ),
    ],
  },
  {
    value: "about",
    title: tr("main_site.layout.footer_menu_about.title"),
    links: [
      menuLink("client.about", tr("main_site.layout.footer_menu_about.about")),
      ...(locale === "be-NL"
        ? [
            menuLink(
              "client.slug",
              tr("main_site.layout.footer_menu_about.faq"),
              { slug: tr("main_site.layout.footer_menu_about.faq_slug") },
            ),
          ]
        : []),
      menuLink(
        "client.jobs.overview",
        tr("main_site.layout.footer_menu_about.vacancies"),
      ),
      menuLink(
        "client.contact",
        tr("main_site.layout.footer_menu_about.contact"),
      ),
    ],
  },
]);
</script>

<style lang="postcss">
.s-footer-menu {
  @apply grow text-white;

  &__title {
    @apply text-lg font-medium text-white;
  }

  &__items {
    @apply m-0 list-none space-y-3 p-0;

    &__link {
      @apply text-base font-medium text-white;

      &:hover {
        @apply underline;
      }
    }
  }

  .p-accordion {
    @apply gap-0;
  }

  .p-accordionheader {
    @apply items-center justify-start px-0 py-4 text-lg font-bold gap-2;
  }

  .p-accordioncontent-content {
    @apply px-0;
  }

  &__columns {
    @apply hidden;
  }

  @container (min-width: 1000px) {
    &__accordion {
      @apply hidden;
    }

    &__columns {
      @apply grid grid-cols-3 gap-8;
    }

    &__column--contact {
      @apply hidden;
    }

    &__column &__title {
      @apply mb-4 font-extrabold;
    }
  }

  @container (min-width: 1300px) {
    &__columns {
      @apply grid-cols-4;
    }

    &__column--contact {
      @apply block;
    }
  }
}
</style>
